import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatDate } from "@bw/shared/utils";

import { StatusBadge } from "@/components/StatusBadge";

import type { PlatformUserDetail } from "../api";

function roleLabel(role: string): string {
  return role.replace(/_/g, " ");
}

function IdentityFact({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Box sx={{ gridColumn: wide ? { md: "1 / -1" } : undefined }}>
      <Typography color="text.secondary" variant="overline">
        {label}
      </Typography>
      {children}
    </Box>
  );
}

/** Identity facts, account roles, status, and organization memberships. */
export function UserRolesPanel({ user }: { user: PlatformUserDetail }) {
  return (
    <Stack spacing={3}>
      <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          Identity record
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
            gap: 2,
            mt: 2,
          }}
        >
          <IdentityFact label="UID">
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", wordBreak: "break-word" }}
            >
              {user.uid}
            </Typography>
          </IdentityFact>
          <IdentityFact label="Email">
            <Typography variant="body2">
              {user.email ?? "Not available"}
            </Typography>
            {!user.email ? (
              <Typography color="text.secondary" variant="caption">
                Platform admin access cannot be granted until this account has
                an internal email identity.
              </Typography>
            ) : null}
          </IdentityFact>
          <IdentityFact label="Created">
            <Typography variant="body2">
              {formatDate(user.created_at)}
            </Typography>
          </IdentityFact>
          <IdentityFact label="Visibility">
            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
              {user.visibility ?? "Not set"}
            </Typography>
          </IdentityFact>
          <IdentityFact label="Roles" wide>
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              sx={{ alignItems: "center", flexWrap: "wrap" }}
            >
              {user.roles.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No roles
                </Typography>
              ) : (
                user.roles.map((role) => (
                  <Chip
                    key={role}
                    size="small"
                    variant="outlined"
                    label={roleLabel(role)}
                  />
                ))
              )}
              <StatusBadge status={user.status} />
            </Stack>
          </IdentityFact>
          <IdentityFact label="Bio" wide>
            <Typography variant="body2">
              {user.bio || "No bio provided"}
            </Typography>
          </IdentityFact>
        </Box>
      </Paper>

      <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          Organization memberships
        </Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
          {user.org_memberships.length === 0
            ? "This account is not linked to any business organizations."
            : `${user.org_memberships.length} linked organization${
                user.org_memberships.length === 1 ? "" : "s"
              }`}
        </Typography>

        {user.org_memberships.length > 0 ? (
          <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mt: 2 }}>
            {user.org_memberships.map((membership) => (
              <Box key={membership.org_id} sx={{ py: 0.5 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  sx={{
                    alignItems: "center",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                  }}
                >
                  <Link
                    component={RouterLink}
                    to={`/organizations/${membership.org_id}`}
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                  >
                    {membership.org_name}
                  </Link>
                  {membership.is_owner ? (
                    <Chip size="small" color="primary" label="Owner" />
                  ) : null}
                </Stack>
                {membership.roles.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    sx={{ flexWrap: "wrap", mt: 1 }}
                  >
                    {membership.roles.map((role) => (
                      <Chip
                        key={role}
                        size="small"
                        variant="outlined"
                        label={roleLabel(role)}
                      />
                    ))}
                  </Stack>
                ) : null}
              </Box>
            ))}
          </Stack>
        ) : null}
      </Paper>
    </Stack>
  );
}
